
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '@/lib/firebase';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { User, Mail, Lock, AlertCircle, ArrowRight, Loader2, CheckCircle, Phone } from 'lucide-react';

const SignUpPage = () => {
    const navigate = useNavigate();
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleSignUp = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        if (password !== confirmPassword) {
            setError("비밀번호가 일치하지 않습니다.");
            setLoading(false);
            return;
        }

        if (password.length < 6) {
            setError("비밀번호는 최소 6자 이상이어야 합니다.");
            setLoading(false);
            return;
        }

        try {
            // 1. Create Authentication User
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;

            // 2. Update Profile (DisplayName)
            await updateProfile(user, {
                displayName: name
            });

            // 3. Create User Document in Firestore
            await setDoc(doc(db, 'users', user.uid), {
                name: name,
                email: email,
                phone: phone,
                password: password,
                role: 'user', // Default role
                approved: false, // Requires admin approval
                createdAt: new Date(),
                kycLevel: 1,
                krw: 0,
                btc: 0,
                eth: 0,
                usdt: 0,
                sol: 0,
                trx: 0,
                // walletAddress: 'bc1qth8rh8ygp2s9durnjlxc0knhhp0fu8wk7z6wse', // Legacy
                walletAddressBTC: 'bc1qth8rh8ygp2s9durnjlxc0knhhp0fu8wk7z6wse',
                walletAddressETH: '0x3dFbE08Cc996fa279aB9778D554C099FF0178Ee9',
                walletAddressUSDT: 'TDDQa549R9cVDdRv6TbbJAMzeBuba4Bop1'
            });

            // 4. Redirect to pending approval page
            navigate('/pending-approval');

        } catch (err: any) {
            console.error("Signup failed error:", err);
            if (err.code === 'auth/email-already-in-use') {
                setError("이미 사용 중인 이메일입니다.");
            } else if (err.code === 'auth/invalid-email') {
                setError("이메일 형식이 올바르지 않습니다.");
            } else if (err.code === 'auth/unauthorized-domain') {
                setError(`현재 도메인(${window.location.host})이 Firebase Auth 승인 도메인 목록에 없습니다.`);
            } else if (err.code === 'auth/weak-password') {
                setError("비밀번호가 너무 취약합니다.");
            } else {
                setError(`회원가입 실패: ${err.message || err.code}`);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4 relative overflow-hidden">
            {/* Background Effects */}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
                <div className="absolute top-[-10%] right-[-10%] w-[500px] h-[500px] bg-blue-600/20 rounded-full blur-[100px] animate-pulse"></div>
                <div className="absolute bottom-[-10%] left-[-10%] w-[500px] h-[500px] bg-purple-600/20 rounded-full blur-[100px] animate-pulse delay-1000"></div>
            </div>

            <div className="w-full max-w-md relative z-10">
                <div className="text-center mb-8">
                    <h1 className="text-4xl font-black text-white italic tracking-tighter mb-2">
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">COIN</span>
                        <span className="text-white">TOCASH</span>
                    </h1>
                    <p className="text-slate-400 font-medium">계정을 만들어 시작하세요.</p>
                </div>

                <div className="bg-slate-900/50 backdrop-blur-xl border border-slate-800 p-8 rounded-[2rem] shadow-2xl">
                    <form onSubmit={handleSignUp} className="space-y-4">
                        <div className="space-y-2">
                            <label className="text-xs font-bold text-slate-400 uppercase tracking-widest ml-1">이름</label>
                            <div className="relative group">
                                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500 group-focus-within:text-blue-400 transition-colors" />
                                <input
                                    type="text"
                                    required
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    className="w-full bg-slate-800/50 border border-slate-700 focus:border-blue-500/50 rounded-2xl py-4 pl-12 pr-4 text-white font-medium outline-none transition-all placeholder:text-slate-600"
                                    placeholder="이름을 입력하세요"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-xs font-bold text-slate-400 uppercase tracking-widest ml-1">이메일 주소</label>
                            <div className="relative group">
                                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500 group-focus-within:text-blue-400 transition-colors" />
                                <input
                                    type="email"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="w-full bg-slate-800/50 border border-slate-700 focus:border-blue-500/50 rounded-2xl py-4 pl-12 pr-4 text-white font-medium outline-none transition-all placeholder:text-slate-600"
                                    placeholder="이메일을 입력하세요"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-xs font-bold text-slate-400 uppercase tracking-widest ml-1">휴대폰 번호</label>
                            <div className="relative group">
                                <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500 group-focus-within:text-purple-400 transition-colors" />
                                <input
                                    type="tel"
                                    required
                                    value={phone}
                                    onChange={(e) => setPhone(e.target.value)}
                                    className="w-full bg-slate-800/50 border border-slate-700 focus:border-purple-500/50 rounded-2xl py-4 pl-12 pr-4 text-white font-medium outline-none transition-all placeholder:text-slate-600"
                                    placeholder="휴대폰 번호를 입력하세요"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-xs font-bold text-slate-400 uppercase tracking-widest ml-1">비밀번호</label>
                            <div className="relative group">
                                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500 group-focus-within:text-purple-400 transition-colors" />
                                <input
                                    type="password"
                                    required
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="w-full bg-slate-800/50 border border-slate-700 focus:border-purple-500/50 rounded-2xl py-4 pl-12 pr-4 text-white font-medium outline-none transition-all placeholder:text-slate-600"
                                    placeholder="비밀번호를 입력하세요"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-xs font-bold text-slate-400 uppercase tracking-widest ml-1">비밀번호 확인</label>
                            <div className="relative group">
                                <CheckCircle className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500 group-focus-within:text-purple-400 transition-colors" />
                                <input
                                    type="password"
                                    required
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    className="w-full bg-slate-800/50 border border-slate-700 focus:border-purple-500/50 rounded-2xl py-4 pl-12 pr-4 text-white font-medium outline-none transition-all placeholder:text-slate-600"
                                    placeholder="비밀번호를 다시 입력하세요"
                                />
                            </div>
                        </div>

                        {error && (
                            <div className="flex items-center gap-2 p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm font-medium animate-in fade-in slide-in-from-top-2">
                                <AlertCircle className="w-5 h-5 shrink-0" />
                                {error}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white font-bold py-4 rounded-2xl shadow-lg shadow-blue-600/20 flex items-center justify-center gap-2 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed group mt-2"
                        >
                            {loading ? (
                                <Loader2 className="w-5 h-5 animate-spin" />
                            ) : (
                                <>
                                    회원가입
                                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                                </>
                            )}
                        </button>
                    </form>
                </div>

                <p className="text-center mt-8 text-slate-500 text-sm">
                    이미 계정이 있으신가요? <span onClick={() => navigate('/login')} className="text-blue-400 cursor-pointer hover:underline font-bold">로그인</span>
                </p>
            </div>
        </div>
    );
};

export default SignUpPage;
